/**
 * Custom cookie/consent UI, ported from forzabuilt-website's CookieConsentManager.tsx.
 * US opt-out model: non-essential tech runs by default; the banner offers
 * Reject Non-Essential / Accept / Cookie Preferences. Every explicit decision
 * is persisted locally and sent to the anonymous server-side consent log.
 *
 * Single-view preferences modal: category rows with toggles; clicking a row
 * (or its arrow) expands it inline to show that category's cookie providers.
 * Styling follows the RuggedRed brand: red #D3242A rounded-full buttons.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONSENT_STORAGE_KEY,
  CONSENT_UPDATED_EVENT,
  OPEN_CONSENT_PREFERENCES_EVENT,
  canLoadAnalytics,
  canLoadMarketing,
  getCurrentConsent,
  gpcEnabled,
  saveConsent,
} from '../../utils/consent';

const CONSENT_BANNER_ENABLED = true;

const modalFocusableSelector =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/* RuggedRed brand tokens */
const INK = '#1A1A1A';
const INK_BODY = 'rgba(26, 26, 26, 0.8)';
const RED = '#D3242A';
const RED_HOVER = '#B31E23';
const TOGGLE_OFF = '#C9C9C9';
const TOGGLE_LOCKED = '#8C8C8C';

/* ---------- Category / provider data ---------- */

// Source of truth for the disclosure copy: update whenever a provider or cookie name changes,
// and register the matching script in public/js/consent.js.
const CATEGORIES = [
  {
    key: 'essential',
    title: 'Essential',
    description:
      'Essential (or strictly necessary) Cookies are required for the operation of the Website. They include, for example, Cookies that enable core site functionality, security, and remembering your privacy choices.',
    locked: true,
    providers: [
      {
        name: 'RuggedRed',
        policyUrl: '/cookie-policy',
        policyLabel: 'ruggedred.com/cookie-policy',
        details:
          'First-party storage used solely to operate the site and honor your privacy choices. Does not store any personally identifiable data.',
        cookies: [
          {
            cookie: CONSENT_STORAGE_KEY,
            duration: 'Persistent (until site data is cleared)',
            description:
              'Stores your cookie consent choices for this browser so the site can honor them on every visit. Browser local storage; contains no personally identifiable data.',
          },
        ],
      },
    ],
  },
  {
    key: 'advertising',
    title: 'Advertising',
    description:
      'Advertising Cookies are used by us and third parties who partner with us to support marketing measurement and business audience insights, and help measure the effectiveness of marketing on our Website.',
    offNote: 'We will not be able to provide you with interest-based offers or marketing insights.',
    emptyNote:
      'We do not currently use any advertising technologies on this website. If we add any, they will be listed here and your choice will apply to them.',
    providers: [],
  },
  {
    key: 'analytics',
    title: 'Analytics & Customization',
    description:
      'Analytics & Customization Cookies allow us to count visits, see how visitors move around the Website, and understand which pages are most useful so we can improve your experience.',
    offNote:
      'We will not be able to remember your preferences or improve your experience based on how you use the Website.',
    providers: [
      {
        name: 'Vercel Analytics',
        policyUrl: 'https://vercel.com/legal/privacy-policy',
        policyLabel: 'vercel.com/legal/privacy-policy',
        details:
          'Cookieless page and event measurement provided by our hosting platform. No cookies are set; anonymous measurement stops when this category is turned off.',
        cookies: [
          {
            cookie: 'None (cookieless)',
            duration: 'Not applicable',
            description:
              'Vercel Analytics measures page views without setting any cookies or storing identifiers in your browser.',
          },
        ],
      },
    ],
  },
];

/* ---------- Icons ---------- */

const SlidersIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="5" y1="4" x2="5" y2="10" />
    <line x1="5" y1="14" x2="5" y2="20" />
    <circle cx="5" cy="12" r="2" />
    <line x1="12" y1="4" x2="12" y2="6" />
    <line x1="12" y1="10" x2="12" y2="20" />
    <circle cx="12" cy="8" r="2" />
    <line x1="19" y1="4" x2="19" y2="12" />
    <line x1="19" y1="16" x2="19" y2="20" />
    <circle cx="19" cy="14" r="2" />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <line x1="5" y1="5" x2="19" y2="19" />
    <line x1="19" y1="5" x2="5" y2="19" />
  </svg>
);

const ChevronIcon = ({ expanded }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      transform: expanded ? 'rotate(90deg)' : 'none',
      transition: 'transform 150ms ease',
    }}
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

/* ---------- Small components ---------- */

const Toggle = ({ checked, disabled = false, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange?.(!checked)}
    className="relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors"
    style={{
      backgroundColor: disabled ? TOGGLE_LOCKED : checked ? RED : TOGGLE_OFF,
      cursor: disabled ? 'not-allowed' : 'pointer',
    }}
  >
    <span
      className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform"
      style={{ transform: checked ? 'translateX(24px)' : 'translateX(4px)' }}
    />
  </button>
);

/* ---------- Main component ---------- */

const CookieConsentManager = () => {
  // src/utils/consent.js has already recorded a GPC opt-out by the time this mounts.
  const [hasDecision, setHasDecision] = useState(() => getCurrentConsent() !== null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedCategory, setExpandedCategory] = useState(null);
  // Draft toggle state for the modal: reloaded from the saved choice every time the modal
  // opens, and only becomes the visitor's choice on Save. Closing without saving discards it.
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(true);
  // Which entry point opened the modal, so a save from this session is logged
  // against the actual flow (footer link, Do Not Sell page) instead of always 'modal'.
  const [preferencesSource, setPreferencesSource] = useState('modal');
  const modalRef = useRef(null);
  const gpc = gpcEnabled();

  const openPreferences = useCallback((source) => {
    const consent = getCurrentConsent();
    // The Do Not Sell page opens the dialog as an opt-out: non-essential categories start
    // off, so saving from there records an opt-out rather than re-confirming the defaults.
    const optOut = source === 'do-not-sell';
    setAnalyticsEnabled(optOut ? false : canLoadAnalytics(consent));
    setMarketingEnabled(optOut ? false : canLoadMarketing(consent));
    setExpandedCategory(null);
    setPreferencesSource(source);
    setIsModalOpen(true);
  }, []);

  useEffect(() => {
    const onOpenPreferences = (event) => openPreferences(event.detail?.source ?? 'modal');
    // A choice saved anywhere (this tab, another tab) ends the first-visit banner, and
    // replaces an open modal's draft so a later Save can't silently undo it.
    const onConsentUpdated = (event) => {
      setHasDecision(true);
      setAnalyticsEnabled(event.detail.analytics);
      setMarketingEnabled(event.detail.marketing);
    };

    window.addEventListener(OPEN_CONSENT_PREFERENCES_EVENT, onOpenPreferences);
    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
    return () => {
      window.removeEventListener(OPEN_CONSENT_PREFERENCES_EVENT, onOpenPreferences);
      window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
    };
  }, [openPreferences]);

  // Autofocus the first focusable element once, when the modal opens.
  useEffect(() => {
    if (!isModalOpen || !modalRef.current) return;
    const focusable = modalRef.current.querySelectorAll(modalFocusableSelector);
    focusable[0]?.focus();
  }, [isModalOpen]);

  // Focus trap + Escape-to-close. Re-queries focusable elements on every Tab
  // press (rather than caching first/last from when the modal opened) so
  // expanding a category — which reveals additional links/buttons — doesn't
  // leave the trap's boundaries stale.
  useEffect(() => {
    if (!isModalOpen) return;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsModalOpen(false);
        return;
      }

      if (event.key !== 'Tab' || !modalRef.current) return;

      const focusable = Array.from(modalRef.current.querySelectorAll(modalFocusableSelector));
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen]);

  const bannerVisible = CONSENT_BANNER_ENABLED && !hasDecision && !bannerDismissed;

  const isCategoryChecked = (key) => {
    if (key === 'essential') return true;
    if (key === 'advertising') return marketingEnabled;
    return analyticsEnabled;
  };

  const setCategoryChecked = (key, next) => {
    if (key === 'advertising') setMarketingEnabled(next);
    if (key === 'analytics') setAnalyticsEnabled(next);
  };

  const toggleExpanded = (key) => {
    setExpandedCategory((prev) => (prev === key ? null : key));
  };

  const handleSave = (choices, source) => {
    if (!saveConsent(choices, source)) return;
    setHasDecision(true);
    setIsModalOpen(false);
  };

  const filledButtonClass =
    'rounded-full px-4 py-2 text-sm font-semibold text-white transition-colors';

  return (
    <>
      {bannerVisible && (
        <aside
          className="fixed bottom-4 right-4 z-[9998] w-[calc(100%-2rem)] max-w-[420px] bg-white shadow-[0_8px_32px_rgba(0,0,0,0.3)] md:bottom-6 md:right-6"
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
        >
          <div className="relative p-5">
            <button
              type="button"
              onClick={() => setBannerDismissed(true)}
              aria-label="Close Cookie Policy"
              className="absolute right-4 top-4 p-1 transition-opacity hover:opacity-70"
              style={{ color: INK }}
            >
              <CloseIcon />
            </button>

            <div className="pr-6">
              <p className="text-sm leading-relaxed md:text-[15px]" style={{ color: INK }}>
                This website uses cookies to ensure you get the best experience on our website.
              </p>
              <Link
                to="/cookie-policy"
                className="mt-2 inline-block text-sm font-semibold underline underline-offset-4 transition-colors hover:text-[#B31E23] md:text-[15px]"
                style={{ color: RED }}
              >
                Cookie Policy
              </Link>
            </div>

            <div className="mt-4 flex flex-col items-start gap-2">
              <button
                type="button"
                onClick={() => handleSave({ analytics: false, marketing: false }, 'banner')}
                className={filledButtonClass}
                style={{ backgroundColor: RED }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = RED_HOVER)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RED)}
              >
                Reject Non-Essential Cookies
              </button>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleSave({ analytics: true, marketing: true }, 'banner')}
                  className={filledButtonClass}
                  style={{ backgroundColor: RED }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = RED_HOVER)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RED)}
                >
                  Accept Cookies
                </button>
                <button
                  type="button"
                  onClick={() => openPreferences('modal')}
                  className="inline-flex items-center justify-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors hover:bg-[#1A1A1A]/5"
                  style={{ borderColor: RED, color: INK }}
                >
                  <SlidersIcon />
                  <span className="underline underline-offset-4">Cookie Preferences</span>
                </button>
              </div>
            </div>
          </div>
        </aside>
      )}

      {isModalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 p-0 md:items-center md:p-6"
          role="presentation"
          onClick={() => setIsModalOpen(false)}
        >
          <div
            ref={modalRef}
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden bg-white p-2"
            role="dialog"
            aria-modal="true"
            aria-label="Your cookie preferences"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="overflow-y-auto p-7 [scrollbar-width:thin] [scrollbar-color:rgba(26,26,26,0.3)_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#1A1A1A]/25 md:p-6 lg:p-10">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-xl font-bold" style={{ color: INK }}>
                  Your Cookie Preferences
                </h2>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  aria-label="Close preferences"
                  className="p-1 transition-opacity hover:opacity-70"
                  style={{ color: INK }}
                >
                  <CloseIcon />
                </button>
              </div>
              <div className="mt-3 h-0.5 w-24" style={{ backgroundColor: RED }} />

              <p className="mt-5 text-sm leading-relaxed" style={{ color: INK_BODY }}>
                We use different types of cookies to optimize your experience on our website. Click
                on the categories below to learn more about their purpose. You may choose which
                types of cookies to allow and can change your preferences at any time. Remember
                that disabling cookies may affect your experience on the website. You can learn
                more about how we use cookies by visiting our{' '}
                <Link
                  to="/cookie-policy"
                  onClick={() => setIsModalOpen(false)}
                  className="font-semibold hover:underline"
                  style={{ color: RED }}
                >
                  Cookie Policy
                </Link>
                . For opt-out rights under applicable U.S. state laws, see{' '}
                <Link
                  to="/do-not-sell-or-share"
                  onClick={() => setIsModalOpen(false)}
                  className="font-semibold hover:underline"
                  style={{ color: RED }}
                >
                  Do Not Sell or Share My Personal Information
                </Link>
                .
              </p>

              <div className="mt-5 flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    setAnalyticsEnabled(true);
                    setMarketingEnabled(true);
                  }}
                  className="text-sm font-semibold underline underline-offset-4 transition-colors hover:text-[#B31E23]"
                  style={{ color: RED }}
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAnalyticsEnabled(false);
                    setMarketingEnabled(false);
                  }}
                  className="text-sm font-semibold underline underline-offset-4 transition-colors hover:text-[#B31E23]"
                  style={{ color: RED }}
                >
                  Unselect All
                </button>
              </div>

              <div className="mt-4">
                {CATEGORIES.map((category) => {
                  const expanded = expandedCategory === category.key;
                  return (
                    <div key={category.key} className="border-b border-[#1A1A1A]/10 py-4 md:py-5">
                      <div className="flex items-start gap-4 md:gap-5">
                        <div className="flex flex-col items-center pt-0.5">
                          <Toggle
                            checked={isCategoryChecked(category.key)}
                            disabled={category.locked}
                            onChange={(next) => setCategoryChecked(category.key, next)}
                            label={
                              category.locked
                                ? `${category.title} (always on)`
                                : `Enable ${category.title}`
                            }
                          />
                          {category.locked && (
                            <span
                              className="mt-1 whitespace-nowrap text-[8px] font-semibold uppercase tracking-wide"
                              style={{ color: INK_BODY }}
                            >
                              Always On
                            </span>
                          )}
                        </div>
                        <div
                          className="min-w-0 flex-1 cursor-pointer"
                          onClick={() => toggleExpanded(category.key)}
                        >
                          <p className="text-base font-semibold" style={{ color: INK }}>
                            {category.title}
                          </p>
                          <p className="mt-1 text-sm leading-relaxed" style={{ color: INK_BODY }}>
                            {category.description}
                          </p>
                          {!isCategoryChecked(category.key) &&
                            !category.locked &&
                            category.offNote && (
                              <p
                                className="mt-3 pl-2 text-sm leading-relaxed"
                                style={{ color: INK_BODY }}
                                role="status"
                              >
                                {category.offNote}
                              </p>
                            )}
                          {category.key === 'advertising' && gpc && (
                            <p
                              className="mt-3 pl-2 text-sm font-semibold leading-relaxed"
                              style={{ color: INK }}
                              role="note"
                            >
                              Your browser is sending a Global Privacy Control signal, which we
                              treat as an opt-out of this category. Turning it on overrides that
                              signal for this browser.
                            </p>
                          )}
                          {expanded && (
                            <div className="mt-3 space-y-3">
                              {category.providers.length === 0 && category.emptyNote && (
                                <div
                                  className="bg-[#1A1A1A]/5 p-3 md:p-4"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <p className="text-xs leading-relaxed" style={{ color: INK_BODY }}>
                                    {category.emptyNote}
                                  </p>
                                </div>
                              )}
                              {category.providers.map((provider) => {
                                const isExternal = provider.policyUrl.startsWith('http');
                                const policyLinkClass =
                                  'mt-0.5 inline-block break-all text-xs underline underline-offset-4 transition-colors hover:text-[#B31E23]';
                                return (
                                  <div
                                    key={provider.name}
                                    className="bg-[#1A1A1A]/5 p-3 md:p-4"
                                    onClick={(event) => event.stopPropagation()}
                                  >
                                    <p className="text-sm font-semibold" style={{ color: INK }}>
                                      {provider.name}
                                    </p>
                                    {isExternal ? (
                                      <a
                                        href={provider.policyUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={policyLinkClass}
                                        style={{ color: RED }}
                                      >
                                        {provider.policyLabel}
                                      </a>
                                    ) : (
                                      <Link
                                        to={provider.policyUrl}
                                        onClick={() => setIsModalOpen(false)}
                                        className={policyLinkClass}
                                        style={{ color: RED }}
                                      >
                                        {provider.policyLabel}
                                      </Link>
                                    )}
                                    <p
                                      className="mt-1.5 text-xs leading-relaxed"
                                      style={{ color: INK_BODY }}
                                    >
                                      {provider.details}
                                    </p>
                                    {provider.cookies?.map((cookieDetail) => (
                                      <div
                                        key={cookieDetail.cookie}
                                        className="mt-3 bg-white p-3 md:p-4"
                                      >
                                        <div className="grid grid-cols-[90px_1fr] gap-x-3 gap-y-2 md:grid-cols-[110px_1fr]">
                                          <p className="text-xs font-bold" style={{ color: INK }}>
                                            Cookie
                                          </p>
                                          <p className="break-all text-xs" style={{ color: INK_BODY }}>
                                            {cookieDetail.cookie}
                                          </p>
                                          <p className="text-xs font-bold" style={{ color: INK }}>
                                            Duration
                                          </p>
                                          <p className="text-xs" style={{ color: INK_BODY }}>
                                            {cookieDetail.duration}
                                          </p>
                                          <p className="text-xs font-bold" style={{ color: INK }}>
                                            Description
                                          </p>
                                          <p
                                            className="text-xs leading-relaxed"
                                            style={{ color: INK_BODY }}
                                          >
                                            {cookieDetail.description}
                                          </p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleExpanded(category.key)}
                          aria-expanded={expanded}
                          aria-label={`${expanded ? 'Hide' : 'Show'} cookie providers for ${category.title}`}
                          className="pt-2 transition-opacity hover:opacity-70"
                          style={{ color: INK }}
                        >
                          <ChevronIcon expanded={expanded} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-shrink-0 flex-col items-stretch gap-3 border-t border-[#1A1A1A]/10 bg-white px-7 py-4 sm:flex-row sm:justify-end md:px-6 lg:px-10">
              <button
                type="button"
                onClick={() =>
                  handleSave(
                    { analytics: analyticsEnabled, marketing: marketingEnabled },
                    preferencesSource,
                  )
                }
                className={filledButtonClass}
                style={{ backgroundColor: RED }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = RED_HOVER)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RED)}
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieConsentManager;
